// AddAgeLimitePaiementRente.jsx
// Modal Add/Edit – appels API intégrés (axios + API_URL/TOKEN)
// POST sans ID | PUT avec ID (depuis selectedItem), doublon UI (age, sexe)
import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants"; // ajuste le chemin si besoin

const DEFAULT_FORM = {
  ageLimite: "",
  age: "",
  sexe: "M",
  prixUnFrancRente: "",
};

const AddAgeLimitePaiementRente = ({
  show,
  handleClose,
  editMode = false,
  selectedItem = null,
  items = [], // pour contrôle doublon en create
  onAdd,      // callback après POST
  onUpdate,   // callback après PUT
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editMode && selectedItem) {
      setFormData({
        ageLimite: selectedItem?.ageLimite ?? "",
        age: selectedItem?.age ?? "",
        sexe: (selectedItem?.sexe ?? "M").toString().toUpperCase(),
        prixUnFrancRente: selectedItem?.prixUnFrancRente ?? "",
      });
    } else {
      setFormData(DEFAULT_FORM);
    }
  }, [editMode, selectedItem, show]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: name === "sexe" ? value.toUpperCase() : value,
    }));
  };

  const validate = () => {
    const errors = [];
    if (String(formData.ageLimite).trim() === "") errors.push("Âge limite requis.");
    if (String(formData.age).trim() === "") errors.push("Âge requis.");
    if (!String(formData.sexe).trim()) errors.push("Sexe requis.");
    if (String(formData.prixUnFrancRente).trim() === "") errors.push("Prix requis.");

    const age = Number(formData.age);
    const ageLimite = Number(formData.ageLimite);
    const prix = Number(formData.prixUnFrancRente);

    if (Number.isNaN(age) || age < 0) errors.push("Âge invalide.");
    if (Number.isNaN(ageLimite) || ageLimite <= 0) errors.push("Âge limite invalide.");
    if (Number.isNaN(prix) || prix < 0) errors.push("Prix invalide.");

    // Doublon UI (age, sexe) en création
    if (!editMode) {
      const dup = items?.some(
        (it) =>
          Number(it.age) === age &&
          String(it.sexe || "").trim().toLowerCase() ===
            String(formData.sexe || "").trim().toLowerCase()
      );
      if (dup) errors.push("Une ligne avec le même couple (âge, sexe) existe déjà.");
    }

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (errors.length) {
      errors.forEach((m) => toast.warning(m));
      return;
    }

    const headers = {
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    };

    try {
      setLoading(true);

      if (editMode) {
        // PUT avec ID provenant de selectedItem
        const payload = {
          ageLimitePaiementRenteId: selectedItem?.ageLimitePaiementRenteId,
          ageLimite: Number(formData.ageLimite),
          age: Number(formData.age),
          sexe: String(formData.sexe || "").trim().toUpperCase(),
          prixUnFrancRente: Number(formData.prixUnFrancRente),
        };

        const { data } = await axios.put(
          `${API_URL}/api/age-limite-paiement-rente`,
          payload,
          { headers }
        );
        toast.success("Ligne modifiée avec succès.");
        onUpdate && onUpdate(data);
      } else {
        // POST sans ID
        const payload = {
          ageLimite: Number(formData.ageLimite),
          age: Number(formData.age),
          sexe: String(formData.sexe || "").trim().toUpperCase(),
          prixUnFrancRente: Number(formData.prixUnFrancRente),
        };

        const { data } = await axios.post(
          `${API_URL}/api/age-limite-paiement-rente`,
          payload,
          { headers }
        );
        toast.success("Ligne ajoutée avec succès.");
        onAdd && onAdd(data);
      }

      setFormData(DEFAULT_FORM);
      handleClose && handleClose();
    } catch (err) {
      console.error(err);
      toast.error(editMode ? "Erreur lors de la modification." : "Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        />
        <div className="text-center mb-4">
          <h4 className="address-title mb-2">
            {editMode ? "Modifier" : "Ajouter"} une ligne
          </h4>
          <p className="address-subtitle">
            {editMode ? "Mettre à jour la ligne sélectionnée" : "Ajouter une nouvelle ligne"}
          </p>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-md-4">
            <label className="form-label">Âge limite</label>
            <input
              type="number"
              name="ageLimite"
              className="form-control"
              value={formData.ageLimite}
              onChange={onChange}
              required
              min={0}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Âge</label>
            <input
              type="number"
              name="age"
              className="form-control"
              value={formData.age}
              onChange={onChange}
              required
              min={0}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Sexe</label>
            <select
              name="sexe"
              className="form-select"
              value={formData.sexe}
              onChange={onChange}
              required
            >
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Prix d'un franc rente</label>
            <input
              type="number"
              step="0.0001"
              min="0"
              name="prixUnFrancRente"
              className="form-control"
              value={formData.prixUnFrancRente}
              onChange={onChange}
              required
            />
          </div>

          <div className="col-12 d-flex gap-2 justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddAgeLimitePaiementRente;
